import openai, { getClientStatus, validateApiKeyFormat, testApiKey } from '../utils/openaiClient.js';

// Rate limiting configuration
const RATE_LIMIT_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  quotaResetTime: 300000, // 5 minutes cooldown after quota exceeded (increased)
  shortCooldown: 30000, // 30 seconds for temporary errors
};

// Global state for rate limiting
let quotaExceeded = false;
let quotaResetTimer = null;
let lastQuotaError = null;
let consecutiveErrors = 0;

/**
 * Implements exponential backoff for API retry logic
 * @param {number} attempt - Current attempt number
 * @returns {number} Delay in milliseconds
 */
function getRetryDelay(attempt) {
  const delay = Math.min(RATE_LIMIT_CONFIG?.baseDelay * Math.pow(2, attempt), RATE_LIMIT_CONFIG?.maxDelay);
  return delay + Math.random() * 1000; // Add jitter to prevent thundering herd
}

/**
 * Resets quota exceeded state after cooldown period
 */
function resetQuotaState() {
  quotaExceeded = false;
  consecutiveErrors = 0;
  lastQuotaError = null;
  
  if (quotaResetTimer) {
    clearTimeout(quotaResetTimer);
    quotaResetTimer = null;
  }
  
  console.info('OpenAI quota state reset - API calls re-enabled');
}

/**
 * Manually resets quota state - useful for testing or user-triggered reset
 */
export function manualQuotaReset() {
  console.info('Manual OpenAI quota reset triggered');
  resetQuotaState();
  return getOpenAIStatus();
}

/**
 * Handles quota exceeded scenarios with proper state management
 */
function handleQuotaExceeded(errorDetails = null) {
  consecutiveErrors++;
  lastQuotaError = {
    timestamp: Date.now(),
    details: errorDetails,
    attempt: consecutiveErrors
  };

  if (!quotaExceeded) {
    quotaExceeded = true;
    const cooldownTime = consecutiveErrors > 3 ? RATE_LIMIT_CONFIG?.quotaResetTime : RATE_LIMIT_CONFIG?.shortCooldown;
    
    console.warn(`OpenAI quota exceeded - entering cooldown mode for ${cooldownTime/1000} seconds`);
    
    // Reset quota state after cooldown
    quotaResetTimer = setTimeout(resetQuotaState, cooldownTime);
    
    return cooldownTime;
  }
  
  return RATE_LIMIT_CONFIG?.quotaResetTime;
}

/**
 * Enhanced API key validation with more detailed checks
 */
function validateApiKey() {
  // First check client initialization status
  const clientStatus = getClientStatus();
  if (!clientStatus?.initialized) {
    return clientStatus?.error;
  }
  
  // Validate format
  const formatValidation = validateApiKeyFormat();
  if (!formatValidation?.valid) {
    return formatValidation;
  }
  
  return { valid: true };
}

/**
 * Enhanced API call wrapper with better error context and recovery
 */
async function makeOpenAIRequest(apiCall, fallbackFunction, operationType = 'AI operation') {
  // Check if we're in quota exceeded cooldown
  if (quotaExceeded) {
    const timeRemaining = quotaResetTimer ? Math.max(0, (lastQuotaError?.timestamp || 0) + RATE_LIMIT_CONFIG?.quotaResetTime - Date.now()) : 0;
    console.warn(`${operationType} skipped - in quota cooldown mode (${Math.ceil(timeRemaining/1000)}s remaining)`);
    return fallbackFunction(true, 'quota_cooldown', { timeRemaining });
  }

  // Validate API key with enhanced checks
  const keyValidation = validateApiKey();
  if (!keyValidation?.valid && keyValidation !== true) {
    console.warn(`${operationType} failed - ${keyValidation?.message}`);
    return fallbackFunction(true, keyValidation?.error, { 
      message: keyValidation?.message,
      suggestion: getAPIKeyFixSuggestion(keyValidation?.error)
    });
  }

  // Test OpenAI client initialization
  if (!openai) {
    console.error(`${operationType} failed - OpenAI client not initialized`);
    return fallbackFunction(true, 'client_error', { message: 'OpenAI client not initialized properly' });
  }

  for (let attempt = 0; attempt < RATE_LIMIT_CONFIG?.maxRetries; attempt++) {
    try {
      console.debug(`${operationType} - attempt ${attempt + 1}`);
      
      const result = await apiCall();
      
      // Success - reset consecutive errors
      if (consecutiveErrors > 0) {
        consecutiveErrors = Math.max(0, consecutiveErrors - 1);
      }
      
      // If successful after retries, log recovery
      if (attempt > 0) {
        console.info(`${operationType} succeeded after ${attempt + 1} attempts`);
      }
      
      return result;
      
    } catch (error) {
      const isLastAttempt = attempt === RATE_LIMIT_CONFIG?.maxRetries - 1;
      
      console.error(`${operationType} error (attempt ${attempt + 1}):`, {
        status: error?.status,
        code: error?.code,
        type: error?.type,
        message: error?.message
      });
      
      // Enhanced error handling for invalid API keys with specific detection
      if (error?.status === 401 || error?.code === 'invalid_api_key' || error?.message?.includes('Incorrect API key')) {
        console.error(`${operationType} failed - invalid or expired API key`);
        
        // Test the API key to get more specific error information
        const testResult = await testApiKey();
        if (!testResult?.success) {
          const apiKeyError = testResult?.error;
          return fallbackFunction(true, 'invalid_key', {
            message: apiKeyError?.message,
            keyIssue: apiKeyError?.type,
            suggestion: getAPIKeyFixSuggestion(apiKeyError?.type),
            recoverySteps: getAPIKeyRecoverySteps(apiKeyError?.type),
            testUrl: 'https://platform.openai.com/account/api-keys'
          });
        }
        
        // Fallback if test doesn't provide specific info
        const apiKey = import.meta.env?.VITE_OPENAI_API_KEY;
        let keyIssue = 'general_invalid';
        let specificMessage = 'API key is invalid, expired, or has insufficient permissions';
        let detailedSuggestion = 'Please verify your API key in the OpenAI dashboard';
        
        // Detect specific key issues
        if (!apiKey || apiKey === 'your-openai-api-key-here') {
          keyIssue = 'missing_key';
          specificMessage = 'No valid OpenAI API key configured';
          detailedSuggestion = 'Add your OpenAI API key to the .env file as VITE_OPENAI_API_KEY';
        } else if (apiKey?.startsWith('sk-proj-')) {
          keyIssue = 'project_key_invalid';
          specificMessage = 'Project-based API key is invalid or expired';
          detailedSuggestion = 'Project keys may have expired or been revoked. Generate a new key from your OpenAI project dashboard';
        } else if (apiKey?.startsWith('sk-') && !apiKey?.includes('proj')) {
          keyIssue = 'legacy_key_invalid';
          specificMessage = 'Legacy API key format detected and invalid';
          detailedSuggestion = 'Consider migrating to project-based API keys for better security and management';
        }
        
        return fallbackFunction(true, 'invalid_key', {
          keyIssue,
          message: specificMessage,
          suggestion: detailedSuggestion,
          apiKeyFormat: apiKey?.startsWith('sk-proj-') ? 'project' : apiKey?.startsWith('sk-') ? 'legacy' : 'unknown',
          recoverySteps: getAPIKeyRecoverySteps(keyIssue),
          testUrl: 'https://platform.openai.com/account/api-keys'
        });
        
      } else if (error?.status === 403) {
        console.error(`${operationType} failed - insufficient permissions`);
        return fallbackFunction(true, 'insufficient_permissions', {
          message: 'API key does not have sufficient permissions',
          suggestion: 'Please check your API key permissions in the OpenAI dashboard',
          dashboardUrl: 'https://platform.openai.com/account/api-keys'
        });
        
      } else if (error?.status === 429 || error?.code === 'rate_limit_exceeded' || error?.code === 'insufficient_quota') {
        const cooldownTime = handleQuotaExceeded({
          status: error?.status,
          code: error?.code,
          type: error?.type,
          message: error?.message
        });
        
        console.warn(`${operationType} failed - quota/rate limit exceeded (attempt ${attempt + 1})`);
        console.info('This is likely due to OpenAI quota limits. Please upgrade your plan or wait for quota reset.');
        
        if (isLastAttempt) {
          return fallbackFunction(true, error?.code === 'insufficient_quota' ? 'insufficient_quota' : 'quota_exceeded', { 
            cooldownTime,
            retryAfter: error?.headers?.['retry-after'] || Math.ceil(cooldownTime / 1000),
            billingUrl: 'https://platform.openai.com/account/billing',
            originalError: {
              status: error?.status,
              code: error?.code,
              type: error?.type,
              message: error?.message
            }
          });
        }
        
        // Wait longer for quota errors
        const waitTime = Math.max(getRetryDelay(attempt) * 2, 5000);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
        
      } else if (error?.status >= 500) {
        console.warn(`${operationType} failed - server error (attempt ${attempt + 1}): ${error?.status}`);
        
        if (isLastAttempt) {
          return fallbackFunction(true, 'server_error', {
            status: error?.status,
            message: 'OpenAI servers are experiencing issues',
            statusUrl: 'https://status.openai.com'
          });
        }
        
        await new Promise(resolve => setTimeout(resolve, getRetryDelay(attempt)));
        continue;
        
      } else {
        // Client errors (4xx) or other errors
        console.error(`${operationType} failed - client/unknown error: ${error?.status || 'unknown'}`, error?.message);
        return fallbackFunction(true, 'client_error', {
          status: error?.status,
          message: error?.message || 'Unknown error occurred',
          code: error?.code
        });
      }
    }
  }
  
  // Fallback if all retries exhausted
  return fallbackFunction(true, 'max_retries_exceeded', {
    attempts: RATE_LIMIT_CONFIG?.maxRetries,
    message: 'Maximum retry attempts exceeded'
  });
}

/**
 * Provides quick fix suggestions based on API key error type
 */
function getAPIKeyFixSuggestion(errorType) {
  switch (errorType) {
    case 'no_api_key':
      return 'Add VITE_OPENAI_API_KEY to your .env file';
    case 'placeholder_key':
      return 'Replace placeholder with actual OpenAI API key';
    case 'invalid_format':
      return 'Ensure key starts with "sk-" and is properly formatted';
    case 'invalid_length':
      return 'Check for incomplete key - OpenAI keys are typically 51+ characters';
    case 'incomplete_project_key':
      return 'Ensure project key is complete (100+ characters starting with sk-proj-)';
    case 'corrupted_key':
      return 'Remove any spaces, newlines, or tabs from the API key';
    default:
      return 'Verify API key in OpenAI dashboard';
  }
}

/**
 * Generates a financial analysis using OpenAI's GPT models with enhanced error handling and rate limiting.
 * @param {Object} financialData - Financial data to analyze.
 * @param {string} analysisType - Type of analysis: 'cash_flow', 'expense', 'revenue', 'compliance', 'anomaly'.
 * @returns {Promise<Object>} AI-generated financial insights.
 */
export async function generateFinancialInsights(financialData, analysisType = 'general') {
  const systemPrompts = {
    cash_flow: 'You are a financial expert specializing in cash flow analysis. Provide actionable insights to optimize cash flow management, payment terms, and working capital.',
    expense: 'You are a cost optimization specialist. Analyze expense patterns, identify cost-saving opportunities, and recommend efficiency improvements.',
    revenue: 'You are a revenue growth strategist. Identify revenue optimization opportunities, customer analysis, and growth strategies.',
    compliance: 'You are a financial compliance expert. Analyze transactions for compliance issues, regulatory requirements, and audit readiness.',
    anomaly: 'You are a financial fraud detection specialist. Identify unusual patterns, potential errors, and anomalies requiring investigation.',
    general: 'You are a comprehensive financial analyst. Provide balanced insights across all financial aspects including trends, risks, and opportunities.'
  };

  const apiCall = async () => {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { 
          role: 'system', 
          content: systemPrompts?.[analysisType] || systemPrompts?.general
        },
        {
          role: 'user',
          content: `Analyze the following financial data and provide specific, actionable insights in JSON format: ${JSON.stringify(financialData)}`
        },
      ],
      max_tokens: 1500,
      temperature: 0.7,
    });

    const content = response?.choices?.[0]?.message?.content;
    try {
      return JSON.parse(content);
    } catch (parseError) {
      return createStructuredResponse(content, analysisType);
    }
  };

  return makeOpenAIRequest(apiCall, getFallbackFinancialInsights, 'Financial analysis');
}

/**
 * Generates sales insights using OpenAI with enhanced error handling.
 * @param {Object} salesData - Sales data to analyze.
 * @returns {Promise<Array>} AI-generated sales insights.
 */
export async function generateSalesInsights(salesData) {
  const apiCall = async () => {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { 
          role: 'system', 
          content: 'You are a sales analytics expert. Analyze sales data to identify opportunities, trends, pricing strategies, and customer insights. Respond in JSON format with an array of insights.' 
        },
        {
          role: 'user',
          content: `Analyze the following sales data and provide actionable insights: ${JSON.stringify(salesData)}`
        },
      ],
      max_tokens: 1000,
      temperature: 0.7,
    });

    const content = response?.choices?.[0]?.message?.content;
    try {
      const result = JSON.parse(content);
      return result?.insights || result || [];
    } catch (parseError) {
      return parseTextToInsights(content, 'sales');
    }
  };

  return makeOpenAIRequest(apiCall, getFallbackSalesInsights, 'Sales analysis');
}

/**
 * Generates document insights using OpenAI with enhanced error handling.
 * @param {Object} documentData - Document data to analyze.
 * @returns {Promise<Array>} AI-generated document insights.
 */
export async function generateDocumentInsights(documentData) {
  const apiCall = async () => {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { 
          role: 'system', 
          content: 'You are a document management expert. Analyze document patterns and provide organization insights in JSON format.' 
        },
        {
          role: 'user',
          content: `Analyze the following document data: ${JSON.stringify(documentData)}`
        },
      ],
      max_tokens: 800,
      temperature: 0.5,
    });

    const content = response?.choices?.[0]?.message?.content;
    try {
      const result = JSON.parse(content);
      return result?.insights || result || [];
    } catch (parseError) {
      return parseTextToInsights(content, 'document');
    }
  };

  return makeOpenAIRequest(apiCall, () => [], 'Document analysis');
}

/**
 * Generates compliance analysis using OpenAI with enhanced error handling.
 * @param {Object} complianceData - Compliance data to analyze.
 * @returns {Promise<Object>} AI-generated compliance insights.
 */
export async function generateComplianceInsights(complianceData) {
  const apiCall = async () => {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { 
          role: 'system', 
          content: 'You are a compliance expert. Analyze compliance data and provide risk assessment in JSON format.' 
        },
        {
          role: 'user',
          content: `Analyze compliance data and provide risk assessment: ${JSON.stringify(complianceData)}`
        },
      ],
      max_tokens: 1000,
      temperature: 0.3,
    });

    const content = response?.choices?.[0]?.message?.content;
    try {
      return JSON.parse(content);
    } catch (parseError) {
      return createStructuredComplianceResponse(content);
    }
  };

  return makeOpenAIRequest(apiCall, getFallbackComplianceInsights, 'Compliance analysis');
}

/**
 * Chat completion for general queries with enhanced error handling.
 * @param {string} userMessage - The user's input message.
 * @param {string} context - Additional context for the query.
 * @returns {Promise<string>} The assistant's response.
 */
export async function getChatCompletion(userMessage, context = 'accounting') {
  const systemPrompts = {
    accounting: 'You are an expert accounting assistant. Help users with accounting questions, financial analysis, and business insights. Be specific and actionable in your responses.',
    general: 'You are a helpful AI assistant. Provide clear, accurate, and helpful responses.'
  };

  const apiCall = async () => {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { 
          role: 'system', 
          content: systemPrompts?.[context] || systemPrompts?.general
        },
        { role: 'user', content: userMessage },
      ],
      max_tokens: 500,
      temperature: 0.7,
    });

    return response?.choices?.[0]?.message?.content || 'I apologize, but I could not generate a response. Please try again.';
  };

  const fallbackResponse = (isError, errorType, errorContext) => {
    const errorMessages = {
      quota_exceeded: `AI chat is temporarily unavailable due to quota limits. ${errorContext?.retryAfter ? `Please try again in ${errorContext?.retryAfter} seconds.` : 'Please upgrade your OpenAI plan or wait for quota reset.'}`,
      quota_cooldown: `AI chat is in cooldown mode. ${errorContext?.timeRemaining ? `Please wait ${Math.ceil(errorContext?.timeRemaining/1000)} seconds.` : 'Please wait a moment before trying again.'}`,
      invalid_key: `AI chat is unavailable. ${errorContext?.message || 'Please check your OpenAI API configuration.'}`,
      no_api_key: 'AI chat is currently unavailable. Please ensure your OpenAI API key is configured properly.',
      placeholder_key: 'Please replace the placeholder OpenAI API key with your actual key.',
      insufficient_permissions: `AI chat access denied. ${errorContext?.message || 'Please check your API key permissions.'}`,
      insufficient_quota: `AI chat unavailable due to quota limits. ${errorContext?.suggestion || 'Please upgrade your OpenAI plan.'}`,
      server_error: `AI chat is temporarily unavailable due to server issues. ${errorContext?.status ? `(Status: ${errorContext?.status})` : ''} Please try again later.`,
      client_error: `There was an issue with your request. ${errorContext?.message || 'Please try rephrasing your question.'}`,
      max_retries_exceeded: `AI chat is experiencing high demand. Tried ${errorContext?.attempts || 3} times. Please try again in a few minutes.`
    };

    return errorMessages?.[errorType] || 'I apologize, but I\'m having trouble processing your request right now. Please try again later.';
  };

  return makeOpenAIRequest(apiCall, fallbackResponse, 'Chat completion');
}

/**
 * Analyzes financial documents using OpenAI with enhanced error handling.
 * @param {string} documentText - Text content of the document.
 * @param {string} documentType - Type of document.
 * @returns {Promise<Object>} Document analysis results.
 */
export async function analyzeFinancialDocument(documentText, documentType = 'general') {
  const apiCall = async () => {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { 
          role: 'system', 
          content: `You are a financial document analysis expert. Extract key information from ${documentType} documents and respond in JSON format.` 
        },
        {
          role: 'user',
          content: `Analyze this ${documentType} document and extract key financial information: ${documentText}`
        },
      ],
      max_tokens: 800,
      temperature: 0.3,
    });

    const content = response?.choices?.[0]?.message?.content;
    try {
      return JSON.parse(content);
    } catch (parseError) {
      return createStructuredDocumentAnalysis(content);
    }
  };

  return makeOpenAIRequest(apiCall, getFallbackDocumentAnalysis, 'Document analysis');
}

/**
 * Gets current OpenAI service status and quota information with client validation
 * @returns {Object} Service status information
 */
export function getOpenAIStatus() {
  const clientStatus = getClientStatus();
  const keyValidation = validateApiKeyFormat();
  const isConfigured = clientStatus?.initialized && keyValidation?.valid;
  
  const status = {
    configured: isConfigured,
    quotaExceeded: quotaExceeded,
    inCooldown: !!quotaResetTimer,
    consecutiveErrors,
    lastError: lastQuotaError,
    cooldownEndsAt: quotaResetTimer && lastQuotaError ? lastQuotaError?.timestamp + RATE_LIMIT_CONFIG?.quotaResetTime : null,
    apiKeyValidation: keyValidation?.valid ? keyValidation : keyValidation,
    clientStatus,
    status: !isConfigured ? 'not_configured' : quotaExceeded ? 'quota_exceeded' : 'available',
    recommendations: getStatusRecommendations(isConfigured, quotaExceeded, keyValidation, clientStatus)
  };
  
  return status;
}

/**
 * Enhanced status recommendations including client initialization status
 * @param {boolean} hasApiKey - Whether API key is configured
 * @param {boolean} quotaExceeded - Whether quota is exceeded
 * @param {Object} keyValidation - API key validation result
 * @param {Object} clientStatus - Client initialization status
 * @returns {Array} Array of recommendation objects
 */
function getStatusRecommendations(hasApiKey, quotaExceeded, keyValidation, clientStatus) {
  // Handle client initialization errors first
  if (!clientStatus?.initialized && clientStatus?.error) {
    return [
      {
        type: 'config',
        title: getErrorTitle(clientStatus?.error?.type),
        description: clientStatus?.error?.message,
        action: getErrorAction(clientStatus?.error?.type),
        urgent: true,
        errorType: clientStatus?.error?.type
      }
    ];
  }
  
  if (!hasApiKey) {
    return [
      {
        type: 'config',
        title: keyValidation?.error === 'placeholder_key' ? 'Replace Placeholder API Key' : 'Configure OpenAI API Key',
        description: keyValidation?.message || 'Add your OpenAI API key to enable AI-powered features',
        action: keyValidation?.error === 'placeholder_key'? 'Replace the placeholder key with your actual OpenAI API key': 'Configure API key in environment variables',
        urgent: true
      }
    ];
  }
  
  if (quotaExceeded) {
    const timeRemaining = lastQuotaError ? Math.max(0, lastQuotaError?.timestamp + RATE_LIMIT_CONFIG?.quotaResetTime - Date.now()) : 0;
    
    return [
      {
        type: 'billing',
        title: 'Upgrade OpenAI Plan',
        description: 'Your current OpenAI quota has been exceeded',
        action: 'Visit OpenAI dashboard to upgrade your plan or add billing information',
        link: 'https://platform.openai.com/account/billing',
        urgent: true
      },
      {
        type: 'wait',
        title: 'Wait for Cooldown Reset',
        description: `AI services will be re-enabled in ${Math.ceil(timeRemaining/1000)} seconds`,
        action: timeRemaining > 0 ? `Wait ${Math.ceil(timeRemaining/1000)} seconds` : 'Services should be available now',
        timeRemaining
      }
    ];
  }
  
  return [
    {
      type: 'success',
      title: 'AI Services Active',
      description: 'All AI-powered features are functioning normally',
      action: null
    }
  ];
}

/**
 * Get error title based on error type
 */
function getErrorTitle(errorType) {
  switch (errorType) {
    case 'no_api_key': return 'OpenAI API Key Missing';
    case 'placeholder_key': return 'Replace Placeholder API Key';
    case 'invalid_format': return 'Invalid API Key Format';
    case 'invalid_length': return 'API Key Too Short';
    case 'incomplete_project_key': return 'Incomplete Project API Key';
    case 'corrupted_key': return 'Corrupted API Key';
    case 'initialization_error': return 'Client Initialization Failed';
    default: return 'OpenAI Configuration Issue';
  }
}

/**
 * Get recommended action based on error type
 */
function getErrorAction(errorType) {
  switch (errorType) {
    case 'no_api_key': return 'Add VITE_OPENAI_API_KEY to your .env file';
    case 'placeholder_key': return 'Replace placeholder with actual OpenAI API key';
    case 'invalid_format': return 'Ensure key starts with "sk-" and is properly formatted';
    case 'invalid_length': return 'Check for incomplete key - OpenAI keys are typically 51+ characters';
    case 'incomplete_project_key': return 'Ensure project key is complete (100+ characters)';
    case 'corrupted_key': return 'Remove any spaces, newlines, or tabs from the API key';
    case 'initialization_error': return 'Check API key format and environment variable configuration';
    default: return 'Verify API key in OpenAI dashboard';
  }
}

// Helper functions for fallback responses (enhanced with better error messaging and detailed context)
function getFallbackFinancialInsights(isError = false, errorType = 'general', errorContext = {}) {
  const errorMessages = {
    quota_exceeded: `AI financial analysis temporarily unavailable due to OpenAI quota limits. ${errorContext?.suggestion || 'Please upgrade your plan or wait for quota reset.'}`,
    insufficient_quota: `AI financial analysis unavailable due to insufficient OpenAI quota. ${errorContext?.suggestion || 'Please upgrade your OpenAI plan or add billing information.'}`,
    quota_cooldown: `AI financial analysis in cooldown mode. ${errorContext?.timeRemaining ? `Please wait ${Math.ceil(errorContext?.timeRemaining/1000)} seconds.` : 'Please wait a moment.'}`,
    invalid_key: `AI financial analysis unavailable. ${errorContext?.message || 'Please configure a valid OpenAI API key.'}`,
    no_api_key: 'AI financial analysis unavailable. Please configure your OpenAI API key.',
    placeholder_key: 'Please replace the placeholder OpenAI API key with your actual key to enable financial analysis.',
    insufficient_permissions: `AI financial analysis unavailable due to insufficient API permissions. ${errorContext?.suggestion || 'Please check your API key permissions.'}`,
    server_error: 'AI financial analysis temporarily unavailable due to OpenAI server issues.',
    client_error: 'AI financial analysis request failed due to client error.',
    max_retries_exceeded: `AI financial analysis unavailable due to high demand. Tried ${errorContext?.attempts || 3} times. Please try again later.`,
    general: 'AI financial analysis temporarily unavailable.'
  };

  const recommendations = getQuotaResolutionSteps(errorType, errorContext);

  return {
    insights: [
      {
        id: 1,
        type: ['quota_exceeded', 'insufficient_quota', 'invalid_key']?.includes(errorType) ? 'warning' : 'neutral',
        title: isError ? 'AI Analysis Unavailable' : 'Manual Review Required',
        description: isError ? errorMessages?.[errorType] : 'Financial data requires manual analysis for detailed insights.',
        recommendation: isError ? recommendations?.[0] : 'Please review your financial data manually or contact your financial advisor.',
        impact: ['quota_exceeded', 'insufficient_quota', 'invalid_key', 'insufficient_permissions']?.includes(errorType) ? 'high' : 'medium',
        confidence: 0,
        actionable: isError && ['quota_exceeded', 'insufficient_quota', 'invalid_key', 'insufficient_permissions']?.includes(errorType),
        category: 'system',
        priority: ['quota_exceeded', 'insufficient_quota', 'invalid_key', 'insufficient_permissions']?.includes(errorType) ? 'high' : 'low',
        actions: isError ? recommendations : [],
        errorDetails: isError ? { 
          type: errorType, 
          context: errorContext,
          timestamp: Date.now(),
          originalError: errorContext?.originalError
        } : null
      }
    ],
    summary: {
      overallHealth: 'unknown',
      keyFindings: [isError ? errorMessages?.[errorType] : 'AI analysis unavailable - manual review recommended'],
      recommendedActions: isError ? recommendations : ['Configure AI service for automated insights', 'Perform manual financial review'],
      riskLevel: 'medium',
      systemStatus: {
        aiEnabled: false,
        errorType: isError ? errorType : null,
        nextSteps: recommendations,
        canRetry: !['invalid_key', 'no_api_key', 'placeholder_key']?.includes(errorType),
        requiresBilling: ['quota_exceeded', 'insufficient_quota']?.includes(errorType)
      }
    }
  };
}

function getFallbackSalesInsights(isError = false, errorType = 'general', errorContext = {}) {
  const recommendations = getQuotaResolutionSteps(errorType, errorContext);
  
  return [
    {
      type: ['quota_exceeded', 'invalid_key', 'insufficient_quota']?.includes(errorType) ? 'warning' : 'neutral',
      category: 'system',
      title: isError ? 'AI Sales Analysis Unavailable' : 'Manual Sales Review',
      description: isError ? `Sales insights temporarily unavailable (${errorType}).` : 'Sales data requires manual analysis.',
      action: isError && recommendations?.length > 0 ? recommendations?.[0] : 'Review sales performance manually',
      impact: ['quota_exceeded', 'invalid_key', 'insufficient_quota']?.includes(errorType) ? 'high' : 'low',
      recommendations: isError ? recommendations : [],
      errorDetails: isError ? { type: errorType, context: errorContext } : null
    }
  ];
}

function getFallbackComplianceInsights(isError = false, errorType = 'general', errorContext = {}) {
  const recommendations = getQuotaResolutionSteps(errorType, errorContext);
  
  return {
    riskAssessment: {
      overallRisk: 'unknown',
      riskFactors: [isError ? `AI compliance analysis unavailable (${errorType})` : 'AI compliance analysis unavailable'],
      recommendations: isError ? recommendations : ['Perform manual compliance review', 'Consult with compliance expert']
    },
    alerts: isError && ['quota_exceeded', 'insufficient_quota']?.includes(errorType) ? [
      {
        type: 'warning',
        message: 'AI compliance monitoring temporarily disabled due to quota limits',
        severity: 'medium'
      }
    ] : [],
    suggestions: [
      {
        category: 'system',
        title: isError && ['quota_exceeded', 'insufficient_quota']?.includes(errorType) ? 'Resolve OpenAI Quota Issue' : 'Enable AI Compliance Analysis',
        description: isError && ['quota_exceeded', 'insufficient_quota']?.includes(errorType) ? 'Upgrade your OpenAI plan to restore AI compliance monitoring' : 'Configure AI services for automated compliance monitoring',
        benefit: 'Automated risk detection and compliance monitoring',
        actions: isError ? recommendations : [],
        errorDetails: isError ? { type: errorType, context: errorContext } : null
      }
    ]
  };
}

function getFallbackDocumentAnalysis(isError = false, errorType = 'general', errorContext = {}) {
  const recommendations = getQuotaResolutionSteps(errorType, errorContext);
  
  return {
    extractedData: {},
    insights: [
      { 
        type: ['quota_exceeded', 'invalid_key', 'insufficient_quota']?.includes(errorType) ? 'warning' : 'info',
        message: isError ? `AI document analysis unavailable (${errorType}). Manual review required.` : 'AI document analysis unavailable. Manual review required.',
        field: 'system',
        recommendations: isError ? recommendations : [],
        errorDetails: isError ? { type: errorType, context: errorContext } : null
      }
    ],
    confidence: 0,
    suggestedActions: isError ? recommendations : ['Manual document review', 'Configure AI services for automated analysis'],
    systemStatus: {
      aiEnabled: false,
      errorType: isError ? errorType : null,
      canRetry: !['invalid_key', 'no_api_key', 'placeholder_key']?.includes(errorType)
    }
  };
}

/**
 * Provides specific recovery steps for different API key issues
 * @param {string} keyIssue - Type of key issue detected
 * @returns {Array} Array of specific recovery steps
 */
function getAPIKeyRecoverySteps(keyIssue) {
  switch (keyIssue) {
    case 'project_key_invalid':
      return [
        '1. Log into your OpenAI account at platform.openai.com',
        '2. Navigate to your specific project dashboard',
        '3. Go to API Keys section within your project',
        '4. Check if your current key is listed and active',
        '5. If expired or missing, generate a new project-based API key',
        '6. Update your .env file with the new key: VITE_OPENAI_API_KEY=sk-proj-...',
        '7. Restart your development server to load the new key',
        '8. Verify the key works by testing a simple API call'
      ];
    case 'legacy_key_invalid':
      return [
        '1. Check if your legacy API key is still valid at platform.openai.com/account/api-keys',
        '2. Consider creating a new project at platform.openai.com',
        '3. Generate a project-based API key for better security',
        '4. Update your environment variable with the new project key',
        '5. Project keys offer better organization and access control',
        '6. Restart your application after updating the key'
      ];
    case 'general_invalid':
      return [
        '1. Verify your API key at platform.openai.com/account/api-keys',
        '2. Check if the key has been revoked or expired',
        '3. Ensure you have sufficient credits/billing set up',
        '4. Generate a new API key if the current one is invalid',
        '5. Copy the new key exactly without spaces or extra characters',
        '6. Update VITE_OPENAI_API_KEY in your .env file',
        '7. Restart your development server'
      ];
    default:
      return [
        '1. Visit platform.openai.com to check your account status',
        '2. Verify your API key is active and properly configured',
        '3. Check your billing and usage limits',
        '4. Generate a new API key if necessary',
        '5. Update your environment variables and restart the application'
      ];
  }
}

/**
 * Enhanced status recommendations with detailed next steps
 */
function getQuotaResolutionSteps(errorType, errorContext = {}) {
  switch (errorType) {
    case 'quota_exceeded': case'insufficient_quota':
      return [
        'Visit your OpenAI dashboard to check current usage and billing status',
        'Add a payment method if you haven\'t already (required for continued usage)',
        'Upgrade to a higher usage tier or add credits to your account',
        errorContext?.retryAfter ? `Wait ${errorContext?.retryAfter} seconds before retrying` : 'Monitor usage limits and set up billing alerts',
        'Consider implementing request optimization to reduce API calls'
      ];
    case 'invalid_key':
      const recoverySteps = errorContext?.recoverySteps || [];
      return recoverySteps?.length > 0 ? recoverySteps : [
        'Check if your OpenAI API key is correct in your environment variables',
        'Verify the API key hasn\'t expired or been revoked in your OpenAI dashboard',
        'Ensure the API key has proper permissions for GPT models',
        'Generate a new API key if the current one is invalid',
        'Update the VITE_OPENAI_API_KEY environment variable and restart the application'
      ];
    case 'missing_key':
      return [
        'Sign up for an OpenAI account at platform.openai.com if you haven\'t already',
        'Generate an API key from https://platform.openai.com/account/api-keys',
        'Add the key to your .env file as VITE_OPENAI_API_KEY=your-actual-key-here',
        'Restart your development server after updating the environment variable',
        'Verify the key works by testing the API integration'
      ];
    case 'placeholder_key':
      return [
        'Sign up for an OpenAI account at platform.openai.com if you haven\'t already',
        'Generate an API key from https://platform.openai.com/account/api-keys',
        'Replace the placeholder key in your .env file with your actual OpenAI API key',
        'Restart your development server after updating the environment variable'
      ];
    case 'corrupted_key':
      return [
        'Open your .env file and check the VITE_OPENAI_API_KEY value',
        'Ensure there are no spaces, line breaks, or extra characters',
        'Copy the key again from https://platform.openai.com/account/api-keys',
        'Paste it directly without formatting changes',
        'Restart your development server'
      ];
    case 'incomplete_project_key':
      return [
        'Check that you copied the complete project-based API key',
        'Project keys are longer than legacy keys (sk-proj-...)',
        'Ensure no characters were truncated when copying the key',
        'Generate a new project key if the current one is incomplete',
        'Update your .env file with the complete key and restart the server'
      ];
    case 'no_api_key':
      return [
        'Create an OpenAI account at platform.openai.com',
        'Navigate to API Keys section in your OpenAI dashboard',
        'Generate a new API key for your application',
        'Add the key to your environment variables as VITE_OPENAI_API_KEY',
        'Restart your application to load the new environment variable'
      ];
    case 'insufficient_permissions':
      return [
        'Log into your OpenAI dashboard and check your API key permissions',
        'Ensure your account has access to the GPT-3.5-turbo model',
        'Verify your account is in good standing with no billing issues',
        'Contact OpenAI support if permissions appear correct but errors persist'
      ];
    case 'quota_cooldown':
      return [
        errorContext?.timeRemaining ? `Wait ${Math.ceil(errorContext?.timeRemaining/1000)} seconds for cooldown to end` : 'Wait for cooldown period to end',
        'Check your OpenAI account for any quota or billing issues',
        'Use manual analysis features in the meantime',
        'Consider upgrading your plan if you frequently hit rate limits'
      ];
    default:
      return [
        'Check OpenAI service status at status.openai.com',
        'Verify your internet connection and firewall settings',
        'Review your OpenAI account dashboard for any notices or issues',
        'Try again in a few minutes as this may be a temporary issue',
        'Contact support if problems persist for more than an hour'
      ];
  }
}

function createStructuredResponse(content, analysisType) {
  return {
    insights: [
      {
        id: 1,
        type: 'neutral',
        title: `${analysisType?.charAt(0)?.toUpperCase() + analysisType?.slice(1)} Analysis`,
        description: content?.slice(0, 200) + '...' || 'Analysis completed',
        recommendation: 'Review the provided analysis for actionable insights',
        impact: 'medium',
        confidence: 75,
        actionable: true,
        category: analysisType,
        priority: 'medium'
      }
    ],
    summary: {
      overallHealth: 'good',
      keyFindings: ['Analysis completed successfully'],
      recommendedActions: ['Review insights and take appropriate action'],
      riskLevel: 'low'
    }
  };
}

function parseTextToInsights(content, type) {
  const sentences = content?.split('.')?.filter(s => s?.trim()?.length > 10) || [];
  return sentences?.slice(0, 3)?.map((sentence, index) => ({
    type: 'neutral',
    category: type,
    title: `${type} Insight ${index + 1}`,
    description: sentence?.trim(),
    impact: 'medium'
  })) || [];
}

function createStructuredComplianceResponse(content) {
  return {
    riskAssessment: {
      overallRisk: 'medium',
      riskFactors: ['Analysis completed with limitations'],
      recommendations: [content?.slice(0, 100) || 'Review compliance status']
    },
    alerts: [],
    suggestions: []
  };
}

function createStructuredDocumentAnalysis(content) {
  return {
    extractedData: {
      summary: content?.slice(0, 100) || 'Document processed'
    },
    insights: [
      { 
        type: 'info', 
        message: content?.slice(0, 200) || 'Document analysis completed',
        field: 'general'
      }
    ],
    confidence: 60,
    suggestedActions: ['Review extracted information', 'Verify accuracy manually']
  };
}